// /people/:id sekmeleri (çizim: personnel.jsx 7b–7g, devam: pdks/admin-c.jsx).
//
// Her sekme kendi modülünün ucunu, o modülün kapısıyla okur; bu yüzden bir
// sekmenin verisi yalnız sekme açıldığında istenir (TabPanel yalnız etkin
// paneli bağlar) ve reddedilen okuma nötr "erişim yok" hâline düşer.
//
// Çizimde olup sözleşmesi olmayanlar çizilmedi: özlük PDF'i, "alan ekle",
// bakmakla yükümlü olunan kişiler (yalnız POST /v1/benefits/dependents var,
// okuma ucu yok), belge yükleme/indirme, zimmet ekleme, oryantasyon adım listesi
// (kayıt sayfasında), bordro dökümü ve geçmişi (bordro modülünün kendi
// ekranları), puantaj PDF'i ve elle giriş.

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";

import {
  Badge,
  Button,
  Card,
  CardBody,
  DataTable,
  FieldRow,
  Progress,
  SectionTitle,
  type DataTableColumn,
} from "@novemai/components";

import { useKeysetQuery } from "@/api/keyset";
import { type ScopeTier } from "@/api/use-scope-tier";
import { useTimeZone } from "@/api/use-time-zone";
import { useSessionScope } from "@/auth/use-session-scope";
import { capabilities as reviewCapabilities } from "@/features/reviews/api";
import { useEffectivePermissions } from "@/features/roles-permissions/use-effective-permissions";
import { DepartmentLink } from "@/components/department-link";
import { PersonLink } from "@/components/person-link";
import { fetchAssignmentsPage, type AssignmentRow } from "@/features/assets/api";
import { fetchEnrollmentsPage, fetchPlansPage, type EnrollmentRow } from "@/features/benefits/api";
import { EmployeeDocumentsTable } from "@/features/documents/employee-documents-table";
import {
  fetchEmployeeLeaveBalancesPage,
  fetchEmployeeLeaveRequestsPage,
  type LeaveBalanceRow,
  type LeaveRequestRow,
} from "@/features/leave/leave-api";
import { formatLeaveSpan } from "@/features/leave/leave-days";
import { displayLeaveStatus, LEAVE_STATUS_TONE } from "@/features/leave/leave-status";
import { fetchOnboardingInstancePage, type OnboardingInstanceRow } from "@/features/onboarding/api";
import { fetchOrgDimensions } from "@/features/org-dimensions/api";
import { fetchEmployeeMasterData } from "@/features/payroll/master-data-api";
import {
  fetchOwnTimesheetsPage,
  fetchWeekDays,
  type TimesheetRow,
} from "@/features/time/time-api";
import { TIMESHEET_STATUS_TONE } from "@/features/time/time-status";
import { WeekDaysPanel } from "@/features/time/timesheet-days-view";
import { currentWeekStart, shiftWeek } from "@/features/time/week";
import { formatDate, formatDateRange } from "@/lib/date-range";
import { formatHours } from "@/lib/hours";

import { type EmployeeRow } from "./api";
import { DirectReportsTable } from "./direct-reports-table";
import { NoAccessState, TabQueryState } from "./detail-tab-state";
import { PersonalInfoCard } from "./personal-info-card";

export const EMPLOYEE_DETAIL_TABS = [
  "ozluk",
  "aile",
  "belgeler",
  "zimmet",
  "izin",
  "devam",
  "bordro",
] as const;
export type EmployeeDetailTab = (typeof EMPLOYEE_DETAIL_TABS)[number];

function SectionCard({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="space-y-3">
      <SectionTitle>{title}</SectionTitle>
      {children}
    </section>
  );
}

/**
 * "Plan a 1:1" and "start a 360" for this person, each shown only when the
 * caller may create one (the same signals the target screens gate on). Both
 * forms take the person as a preset and still let the server decide whether the
 * pair or subject is allowed.
 */
export function PersonActions({ employeeId }: { readonly employeeId: string }): React.ReactElement | null {
  const { t } = useTranslation();
  const scope = useSessionScope();
  const { capabilities: cells } = useEffectivePermissions();
  const reviewCaps = useQuery({ queryKey: ["review-capabilities", ...scope], queryFn: reviewCapabilities });
  const canMeet = cells.has("performance.create");
  const canReview = reviewCaps.data?.can_create === true;
  if (!canMeet && !canReview) return null;
  const id = encodeURIComponent(employeeId);
  return (
    <>
      {canMeet ? (
        <Button variant="secondary" asChild>
          <RouterLink to={`/me/one-on-ones?participant=${id}`}>{t("people.detail.plan_one_on_one")}</RouterLink>
        </Button>
      ) : null}
      {canReview ? (
        <Button variant="secondary" asChild>
          <RouterLink to={`/me/reviews/new?subject=${id}`}>{t("people.detail.start_review")}</RouterLink>
        </Button>
      ) : null}
    </>
  );
}

// ---- Özlük -------------------------------------------------------------------

export function PersonnelFileTab({ row }: { readonly row: EmployeeRow }): React.ReactElement {
  const { t, i18n } = useTranslation();
  const none = t("people.detail.none");
  return (
    <div className="space-y-6">
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <Card>
          <CardBody>
            <SectionTitle>{t("people.detail.section_job")}</SectionTitle>
            <FieldRow label={t("people.detail.field_position")} value={row.job_title ?? none} />
            <FieldRow
              label={t("people.detail.field_employment_type")}
              value={
                row.employment_type !== null
                  ? t(`people.employment_type.${row.employment_type}`)
                  : none
              }
            />
            <FieldRow
              label={t("people.detail.field_start_date")}
              value={row.start_date !== null ? formatDate(i18n.language, row.start_date) : none}
            />
            <FieldRow
              label={t("people.detail.field_department")}
              value={
                row.department_name === null ? (
                  none
                ) : (
                  <DepartmentLink departmentId={row.department_id}>
                    {row.department_name}
                  </DepartmentLink>
                )
              }
            />
            <FieldRow
              label={t("people.detail.field_manager")}
              value={
                // Linked only when the NAME resolved: a bare id would put the
                // absent-marker under the link and offer to open "—".
                row.manager_name === null ? (
                  none
                ) : (
                  <PersonLink employeeId={row.manager_id}>{row.manager_name}</PersonLink>
                )
              }
            />
          </CardBody>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardBody>
              <SectionTitle>{t("people.detail.section_contact")}</SectionTitle>
              <FieldRow label={t("people.detail.field_email")} value={row.primary_work_email} />
            </CardBody>
          </Card>
          <CustomFieldsCard values={row.org_dimension_values} />
        </div>
      </div>

      <SectionCard title={t("people.detail.section_team")}>
        <DirectReportsTable managerId={row.employee_id} />
      </SectionCard>
    </div>
  );
}

/**
 * The tenant's org dimensions (ADR-012) the person carries. Labelled from the
 * dimension catalogue; when that read fails the card is left out rather than
 * printing bare slugs as if they were labels.
 */
function CustomFieldsCard({
  values,
}: {
  readonly values: Readonly<Record<string, string>> | null;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const entries = Object.entries(values ?? {});
  const catalog = useQuery({
    queryKey: ["org-dimensions", "employee-detail"],
    queryFn: () => fetchOrgDimensions({ state: "all" }),
    enabled: entries.length > 0,
  });
  if (entries.length === 0 || catalog.data === undefined) return null;
  const nameOf = new Map(catalog.data.map((d) => [d.slug, d.name]));
  const labelled = entries.filter(([slug]) => nameOf.has(slug));
  if (labelled.length === 0) return null;
  return (
    <Card>
      <CardBody>
        <SectionTitle>{t("people.detail.section_custom_fields")}</SectionTitle>
        {labelled.map(([slug, value]) => (
          <FieldRow key={slug} label={nameOf.get(slug)} value={value} />
        ))}
      </CardBody>
    </Card>
  );
}

// ---- Aile & acil durum ---------------------------------------------------------

export function FamilyTab({
  employeeId,
  tier,
}: {
  readonly employeeId: string;
  readonly tier: ScopeTier | undefined;
}): React.ReactElement {
  // Telefon, ev adresi ve acil durum kişileri yalnız İK katmanının: bir yönetici
  // astının kaydını okur ama ev adresini okuyamaz. Diğer katmanlar için istek
  // hiç atılmaz; uç zaten ikinci kapıdır.
  if (tier !== "executive_broad") return <NoAccessState />;
  return (
    <PersonalInfoCard source={{ kind: "hr", employeeId }} absent={<NoAccessState />} />
  );
}

// ---- Belgeler ------------------------------------------------------------------

export function DocumentsTab({ employeeId }: { readonly employeeId: string }): React.ReactElement {
  return <EmployeeDocumentsTable employeeId={employeeId} />;
}

// ---- Zimmet & oryantasyon ------------------------------------------------------

export function AssetsTab({ employeeId }: { readonly employeeId: string }): React.ReactElement {
  const { t, i18n } = useTranslation();
  const none = t("people.detail.none");
  const assignments = useKeysetQuery<AssignmentRow>({
    queryKey: ["assets", "assignments", "employee", employeeId],
    fetchPage: (cursor) => fetchAssignmentsPage(cursor, { employee_id: employeeId }),
  });
  const onboarding = useKeysetQuery<OnboardingInstanceRow>({
    queryKey: ["onboarding", "instances", "employee", employeeId],
    fetchPage: (cursor) => fetchOnboardingInstancePage(cursor, { employeeId }),
  });

  const assignmentColumns: readonly DataTableColumn<AssignmentRow>[] = [
    {
      id: "asset",
      header: t("people.assets.column.asset"),
      cell: (row): React.ReactNode => (
        <div>
          <div className="text-[13.5px] font-medium text-[var(--fg-primary)]">
            {row.model_name ?? none}
          </div>
          <div className="font-mono text-[11px] text-[var(--fg-muted)]">
            {row.asset_tag ?? row.serial_number ?? none}
          </div>
        </div>
      ),
    },
    {
      id: "assigned",
      header: t("people.assets.column.assigned_at"),
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap text-[12.5px] text-[var(--fg-muted)]">
          {row.assigned_at !== null ? formatDate(i18n.language, row.assigned_at) : none}
        </span>
      ),
    },
    {
      id: "state",
      header: t("people.assets.column.state"),
      cell: (row): React.ReactNode => (
        <Badge tone={row.state === "active" ? "success" : "neutral"}>
          {t(`assets.state.${row.state}`)}
        </Badge>
      ),
    },
  ];

  const onboardingColumns: readonly DataTableColumn<OnboardingInstanceRow>[] = [
    {
      id: "start",
      header: t("people.assets.column.onboarding_start"),
      cell: (row): React.ReactNode => (
        <RouterLink
          to={`/onboarding/instances/${row.instance_id}`}
          className="text-[13px] text-[var(--accent)] hover:underline"
        >
          {formatDate(i18n.language, row.start_date)}
        </RouterLink>
      ),
    },
    {
      id: "progress",
      header: t("people.assets.column.onboarding_progress"),
      cell: (row): React.ReactNode => (
        <Progress
          value={Number(row.completion_percent)}
          aria-label={t("people.assets.column.onboarding_progress")}
        />
      ),
    },
    {
      id: "status",
      header: t("people.assets.column.state"),
      cell: (row): React.ReactNode => (
        <div className="flex flex-wrap gap-1.5">
          <Badge tone={row.status === "completed" ? "success" : "info"}>
            {t(`onboarding.status.${row.status}`)}
          </Badge>
          {row.at_risk ? <Badge tone="warning">{t("onboarding.at_risk_yes")}</Badge> : null}
        </div>
      ),
    },
  ];

  return (
    <div className="grid items-start gap-6 lg:grid-cols-2">
      <SectionCard title={t("people.assets.assignments_title")}>
        <TabQueryState
          query={assignments}
          isEmpty={assignments.items.length === 0}
          emptyTitle={t("people.assets.assignments_empty")}
        >
          <Card className="overflow-hidden">
            <DataTable<AssignmentRow>
              caption={t("people.assets.assignments_title")}
              columns={assignmentColumns}
              rows={assignments.items}
              rowKey={(row): string => row.assignment_id}
              hasNextPage={assignments.hasNextPage}
              onLoadMore={assignments.fetchNextPage}
              isFetchingNextPage={assignments.isFetchingNextPage}
              loadMoreLabel={t("people.detail.tabs.load_more")}
            />
          </Card>
        </TabQueryState>
      </SectionCard>
      <SectionCard title={t("people.assets.onboarding_title")}>
        <TabQueryState
          query={onboarding}
          isEmpty={onboarding.items.length === 0}
          emptyTitle={t("people.assets.onboarding_empty")}
        >
          <Card className="overflow-hidden">
            <DataTable<OnboardingInstanceRow>
              caption={t("people.assets.onboarding_title")}
              columns={onboardingColumns}
              rows={onboarding.items}
              rowKey={(row): string => row.instance_id}
              hasNextPage={onboarding.hasNextPage}
              onLoadMore={onboarding.fetchNextPage}
              isFetchingNextPage={onboarding.isFetchingNextPage}
              loadMoreLabel={t("people.detail.tabs.load_more")}
            />
          </Card>
        </TabQueryState>
      </SectionCard>
    </div>
  );
}

// ---- İzin & talepler -----------------------------------------------------------

export function LeaveTab({ employeeId }: { readonly employeeId: string }): React.ReactElement {
  const { t, i18n } = useTranslation();
  const none = t("people.detail.none");
  const balances = useKeysetQuery<LeaveBalanceRow>({
    queryKey: ["leave", "balances", "employee", employeeId],
    fetchPage: (cursor) => fetchEmployeeLeaveBalancesPage(employeeId, cursor),
  });
  const requests = useKeysetQuery<LeaveRequestRow>({
    queryKey: ["leave", "requests", "employee", employeeId],
    fetchPage: (cursor) => fetchEmployeeLeaveRequestsPage(employeeId, cursor),
  });
  // The server pages oldest first; the history reads newest first over the
  // loaded window, and "load more" extends that window.
  const requestRows = useMemo(
    () => [...requests.items].sort((a, b) => b.start_date.localeCompare(a.start_date)),
    [requests.items],
  );
  const span = (hours: string): string => formatLeaveSpan(hours, i18n.language, t) ?? hours;

  const balanceColumns: readonly DataTableColumn<LeaveBalanceRow>[] = [
    {
      id: "policy",
      header: t("leave.balance.column.policy"),
      cell: (row): React.ReactNode => (
        <span className="text-[13px] text-[var(--fg-primary)]">{row.policy_name ?? none}</span>
      ),
    },
    {
      id: "balance",
      header: t("leave.balance.column.balance_hours"),
      align: "end",
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums text-[13px]">{span(row.balance_hours)}</span>
      ),
    },
    {
      id: "used",
      header: t("people.leave.column.used"),
      align: "end",
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums text-[13px] text-[var(--fg-secondary)]">
          {row.used_hours !== undefined ? span(row.used_hours) : none}
        </span>
      ),
    },
    {
      id: "pending",
      header: t("people.leave.column.pending"),
      align: "end",
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums text-[13px] text-[var(--fg-secondary)]">
          {row.pending_hours !== undefined ? span(row.pending_hours) : none}
        </span>
      ),
    },
  ];

  const requestColumns: readonly DataTableColumn<LeaveRequestRow>[] = [
    {
      id: "type",
      header: t("leave.column.leave_type"),
      cell: (row): React.ReactNode => (
        <span className="text-[13px] font-medium text-[var(--fg-primary)]">
          {row.policy_name ?? none}
        </span>
      ),
    },
    {
      id: "dates",
      header: t("leave.column.dates"),
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap text-[12.5px] text-[var(--fg-secondary)]">
          {formatDateRange(i18n.language, row.start_date, row.end_date)}
        </span>
      ),
    },
    {
      id: "hours",
      header: t("leave.column.hours"),
      align: "end",
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums text-[13px]">{span(row.hours)}</span>
      ),
    },
    {
      id: "status",
      header: t("leave.column.status"),
      cell: (row): React.ReactNode => {
        const shown = displayLeaveStatus(row);
        return (
          <Badge tone={LEAVE_STATUS_TONE[shown] ?? "neutral"}>{t(`leave.status.${shown}`)}</Badge>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <SectionCard title={t("people.leave.balances_title")}>
        <TabQueryState
          query={balances}
          isEmpty={balances.items.length === 0}
          emptyTitle={t("people.leave.balances_empty")}
        >
          <Card className="overflow-hidden">
            <DataTable<LeaveBalanceRow>
              caption={t("people.leave.balances_title")}
              columns={balanceColumns}
              rows={balances.items}
              rowKey={(row): string => row.balance_id}
              hasNextPage={balances.hasNextPage}
              onLoadMore={balances.fetchNextPage}
              isFetchingNextPage={balances.isFetchingNextPage}
              loadMoreLabel={t("people.detail.tabs.load_more")}
            />
          </Card>
        </TabQueryState>
      </SectionCard>
      <SectionCard title={t("people.leave.requests_title")}>
        <TabQueryState
          query={requests}
          isEmpty={requests.items.length === 0}
          emptyTitle={t("people.leave.requests_empty")}
        >
          <Card className="overflow-hidden">
            <DataTable<LeaveRequestRow>
              caption={t("people.leave.requests_title")}
              columns={requestColumns}
              rows={requestRows}
              rowKey={(row): string => row.request_id}
              hasNextPage={requests.hasNextPage}
              onLoadMore={requests.fetchNextPage}
              isFetchingNextPage={requests.isFetchingNextPage}
              loadMoreLabel={t("people.detail.tabs.load_more")}
            />
          </Card>
        </TabQueryState>
      </SectionCard>
    </div>
  );
}

// ---- Devam (PDKS) ----------------------------------------------------------------

/**
 * Read-only: the week view and the person's sheets. Sign-off and approval stay
 * on the employee's own card and the PDKS queue; this page only shows them.
 */
export function AttendanceTab({ employeeId }: { readonly employeeId: string }): React.ReactElement {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const thisWeek = useMemo(() => currentWeekStart(new Date(), timeZone), [timeZone]);
  const [chosenWeek, setChosenWeek] = useState<string | null>(null);
  const weekStart = chosenWeek ?? thisWeek;

  const week = useQuery({
    queryKey: ["time", "week-days", employeeId, weekStart],
    queryFn: () => fetchWeekDays(weekStart, employeeId),
  });
  const sheets = useKeysetQuery<TimesheetRow>({
    queryKey: ["time", "timesheets", "employee", employeeId],
    fetchPage: (cursor) => fetchOwnTimesheetsPage(employeeId, cursor),
  });
  const sheetRows = useMemo(
    () => [...sheets.items].sort((a, b) => b.period_start.localeCompare(a.period_start)),
    [sheets.items],
  );
  const hours = (value: string): string => formatHours(value, i18n.language) ?? value;

  const sheetColumns: readonly DataTableColumn<TimesheetRow>[] = [
    {
      id: "period",
      header: t("time.column.period"),
      cell: (row): string => formatDateRange(i18n.language, row.period_start, row.period_end),
    },
    {
      id: "regular",
      header: t("time.column.regular_hours"),
      align: "end",
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums">{hours(row.total_regular_hours)}</span>
      ),
    },
    {
      id: "ot",
      header: t("time.column.ot_hours"),
      align: "end",
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums">{hours(row.total_ot_hours)}</span>
      ),
    },
    {
      id: "leave",
      header: t("time.column.leave_hours"),
      align: "end",
      cell: (row): React.ReactNode => (
        <span className="font-mono tabular-nums">{hours(row.total_pto_hours)}</span>
      ),
    },
    {
      id: "status",
      header: t("time.column.status"),
      cell: (row): React.ReactNode => (
        <Badge tone={TIMESHEET_STATUS_TONE[row.status] ?? "neutral"}>
          {t(`time.timesheet_status.${row.status}`)}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <SectionCard title={t("people.attendance.week_title")}>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            aria-label={t("people.attendance.previous_week")}
            onClick={(): void => {
              setChosenWeek(shiftWeek(weekStart, -1));
            }}
          >
            <ChevronLeft size={14} aria-hidden="true" />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={chosenWeek === null}
            onClick={(): void => {
              setChosenWeek(null);
            }}
          >
            {t("people.attendance.this_week")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            aria-label={t("people.attendance.next_week")}
            onClick={(): void => {
              setChosenWeek(shiftWeek(weekStart, 1));
            }}
          >
            <ChevronRight size={14} aria-hidden="true" />
          </Button>
        </div>
        <TabQueryState query={week} isEmpty={false} emptyTitle="">
          {week.data !== undefined ? (
            <Card>
              <CardBody>
                <WeekDaysPanel view={week.data} />
              </CardBody>
            </Card>
          ) : null}
        </TabQueryState>
      </SectionCard>
      <SectionCard title={t("people.attendance.sheets_title")}>
        <TabQueryState
          query={sheets}
          isEmpty={sheets.items.length === 0}
          emptyTitle={t("people.attendance.sheets_empty")}
        >
          <Card className="overflow-hidden">
            <DataTable<TimesheetRow>
              caption={t("people.attendance.sheets_title")}
              columns={sheetColumns}
              rows={sheetRows}
              rowKey={(row): string => row.timesheet_id}
              hasNextPage={sheets.hasNextPage}
              onLoadMore={sheets.fetchNextPage}
              isFetchingNextPage={sheets.isFetchingNextPage}
              loadMoreLabel={t("people.detail.tabs.load_more")}
            />
          </Card>
        </TabQueryState>
      </SectionCard>
    </div>
  );
}

// ---- Bordro & yan haklar -------------------------------------------------------

export function PayrollTab({ employeeId }: { readonly employeeId: string }): React.ReactElement {
  const { t } = useTranslation();
  const none = t("people.detail.none");
  const master = useQuery({
    queryKey: ["payroll", "master-data", employeeId],
    queryFn: () => fetchEmployeeMasterData(employeeId),
    retry: false,
  });
  const enrollments = useKeysetQuery<EnrollmentRow>({
    queryKey: ["benefits", "enrollments", "employee", employeeId],
    fetchPage: (cursor) => fetchEnrollmentsPage(cursor, { employee_id: employeeId }),
  });
  // Plan names come from the catalogue's first page; a plan beyond it renders
  // the absent marker rather than its id.
  const plans = useQuery({
    queryKey: ["benefits", "plans", "first-page"],
    queryFn: () => fetchPlansPage(null),
    enabled: enrollments.items.length > 0,
  });
  const planName = new Map((plans.data?.items ?? []).map((p) => [p.plan_id, p.name]));

  const data = master.data;
  const enrollmentColumns: readonly DataTableColumn<EnrollmentRow>[] = [
    {
      id: "plan",
      header: t("benefits.column_plan"),
      cell: (row): React.ReactNode => (
        <span className="text-[13px] text-[var(--fg-primary)]">
          {planName.get(row.plan_id) ?? none}
        </span>
      ),
    },
    {
      id: "tier",
      header: t("benefits.column_tier"),
      cell: (row): React.ReactNode => (
        <span className="text-[13px] text-[var(--fg-secondary)]">
          {t(`benefits.tier.${row.tier}`, { defaultValue: none })}
        </span>
      ),
    },
    {
      id: "election",
      header: t("benefits.column_election"),
      cell: (row): React.ReactNode => (
        <Badge tone={row.election_status === "active" ? "success" : "neutral"}>
          {t(`benefits.election_status.${row.election_status}`)}
        </Badge>
      ),
    },
  ];

  return (
    <div className="grid items-start gap-6 lg:grid-cols-2">
      <SectionCard title={t("people.payroll.master_title")}>
        <TabQueryState query={master} isEmpty={false} emptyTitle="">
          {data !== undefined ? (
            <Card>
              <CardBody>
                {/* TCKN ve IBAN değerleri burada YAZILMAZ: yalnız kayıtlı olup
                    olmadıkları. Değerin kendisi bordro ana veri ekranındadır. */}
                <FieldRow
                  label={t("payroll_master.tckn")}
                  value={t(data.tckn !== null ? "people.payroll.on_file" : "people.payroll.missing")}
                />
                <FieldRow
                  label={t("payroll_master.iban")}
                  value={t(
                    data.bank_account !== null ? "people.payroll.on_file" : "people.payroll.missing",
                  )}
                />
                <FieldRow
                  label={t("payroll_master.bank_name")}
                  value={data.bank_account?.bank_name ?? none}
                />
                <FieldRow
                  label={t("payroll_master.education")}
                  value={
                    data.education_level !== null
                      ? t(`payroll.tr.education.${data.education_level}`)
                      : none
                  }
                />
                <FieldRow
                  label={t("payroll_master.union_status")}
                  value={t(`payroll_master.union_status_${data.union_status}`)}
                />
                <FieldRow
                  label={t("payroll_master.multiple_employers")}
                  value={t(data.has_multiple_employers ? "people.payroll.yes" : "people.payroll.no")}
                />
                {data.has_multiple_employers && (
                  <FieldRow
                    label={t("payroll_master.highest_paying_employer")}
                    value={t(
                      data.is_highest_paying_employer ? "people.payroll.yes" : "people.payroll.no",
                    )}
                  />
                )}
                <FieldRow
                  label={t("payroll_master.retired")}
                  value={t(data.is_retired ? "people.payroll.yes" : "people.payroll.no")}
                />
                <div className="pt-3">
                  <Button variant="secondary" size="sm" asChild>
                    <RouterLink to="/payroll/employees">{t("people.payroll.open_master")}</RouterLink>
                  </Button>
                </div>
              </CardBody>
            </Card>
          ) : null}
        </TabQueryState>
      </SectionCard>
      <SectionCard title={t("people.payroll.benefits_title")}>
        <TabQueryState
          query={enrollments}
          isEmpty={enrollments.items.length === 0}
          emptyTitle={t("people.payroll.benefits_empty")}
        >
          <Card className="overflow-hidden">
            <DataTable<EnrollmentRow>
              caption={t("people.payroll.benefits_title")}
              columns={enrollmentColumns}
              rows={enrollments.items}
              rowKey={(row): string => row.enrollment_id}
              hasNextPage={enrollments.hasNextPage}
              onLoadMore={enrollments.fetchNextPage}
              isFetchingNextPage={enrollments.isFetchingNextPage}
              loadMoreLabel={t("people.detail.tabs.load_more")}
            />
          </Card>
        </TabQueryState>
      </SectionCard>
    </div>
  );
}
